import type { TFunction } from 'i18next';

import { formatNameList } from '../../../../utilities/formatList';
import { modeEffectLabel } from '../../constants/labels';
import type { DeliveryReach } from '../../domain/settings';
import type { RowNoteKey, RowWarningKey } from '../../domain/severityRows';
import type { DeliveryMode, NoOperatorPlant } from '../../types/domain.types';

export const rowWarningText = (t: TFunction, key: RowWarningKey): string => {
  switch (key) {
    case 'MAJOR_DROPPING_OFF':
      return t('closeTheLoop.settings.majorDroppingOff', 'Your Leads and the site admin will be told you’ve turned Major off.');
    case 'MAJOR_DROPPING_DIGEST':
      return t('closeTheLoop.settings.majorDroppingDigest', 'Your Leads and the site admin will be told you’ve turned Major down.');
  }
};

export const rowNoteText = (t: TFunction, key: RowNoteKey): string => {
  switch (key) {
    case 'EMERGENCY_LOCKED':
      return t(
        'closeTheLoop.settings.emergencyLocked',
        'Always on: a phone call and a full-screen alarm, plus your plant’s hooter if it’s on.',
      );
    case 'LOCKED':
      return t('closeTheLoop.settings.lockedByRole', 'Set by your role.');
    case 'VIEWER_MAJOR':
      return t('closeTheLoop.settings.viewerMajor', 'Morning email: once it’s overdue.');
    case 'VIEWER_MAJOR_CHRONIC':
      return t('closeTheLoop.settings.viewerMajorChronic', 'Morning email: once it keeps coming back.');
    case 'MAJOR_IS_DOWN':
      return t('closeTheLoop.settings.majorNotRightAway', 'You won’t get Major alerts right away.');
  }
};

export const rowEffectText = (t: TFunction, mode: DeliveryMode, reach: DeliveryReach, locale: string): string =>
  mode === 'DIGEST' && !reach.emailVerified
    ? t('closeTheLoop.settings.effectDigestInAppUnverified', 'In Recent alerts, and by morning email once your email is verified')
    : modeEffectLabel(t, mode, reach.channel, reach.digestAtMinute, locale);

export const saveLabel = (t: TFunction, majorDropping: boolean): string =>
  majorDropping
    ? t('closeTheLoop.settings.saveAndTell', 'Save and tell Leads and site admin')
    : t('closeTheLoop.settings.save', 'Save changes');

export const saveDirtyMessage = (t: TFunction, majorDropping: boolean): string | undefined =>
  majorDropping ? t('closeTheLoop.settings.saveTells', 'Saving tells your Leads and the site admin.') : undefined;

export const noOperatorText = (t: TFunction, plants: readonly NoOperatorPlant[], locale: string): string => {
  const names = formatNameList(
    plants.map((plant) => plant.plantName),
    locale,
  );
  return plants.length === 1
    ? t(
        'closeTheLoop.settings.noOperatorOne',
        'Nobody works as an Operator at {{plants}}, so every new Emergency, Major and Minor alert there reaches you right away, whatever you choose below.',
        { plants: names },
      )
    : t(
        'closeTheLoop.settings.noOperatorMany',
        'Nobody works as an Operator at {{plants}}, so every new Emergency, Major and Minor alert at those plants reaches you right away, whatever you choose below.',
        { plants: names },
      );
};
